/**
 * Fokusziel für Fixed-Bottom-Banner nach dem Ausblenden (Cookie-Consent-
 * und Install-App-Banner, siehe CookieConsentBanner.js/InstallAppBanner.js).
 *
 * Ohne dieses Modul würde der Fokus nach dem Entfernen des Banner-Buttons
 * kommentarlos auf <body> "verloren gehen" — für Screenreader-Nutzer nicht
 * nachvollziehbar. MAJOR-4-Fix: ursprünglich prüfte InstallAppBanner.js nur
 * #main-content; da beide Banner auch auf den eigenständigen Marketing-
 * Seiten (Landing, Datenschutz, AGB, MCP) erscheinen, wo es #main-content
 * nicht gibt (siehe moveFocusAfterDismiss unten), wurde die vollständige
 * Logik hierher extrahiert, um Drift zwischen beiden Bannern zu vermeiden.
 *
 * @module focusAfterDismiss
 */

/**
 * Explizite Fokusziele der eigenständigen Marketing-Seiten: ein reiner
 * querySelector('h1')-Fallback ist fragil, weil er nur solange
 * funktioniert, wie jede Seite genau ein <h1> hat, das zugleich das erste
 * im DOM ist. Robuster ist eine explizite ID-Liste — querySelector('h1')
 * bleibt als letzter Fallback für neue/vergessene Marketing-Seiten erhalten.
 * IDs müssen zu PUBLIC_MARKETING_ROUTES (publicRoutes.js) passen:
 *  - '/'            -> LandingPage.js   #landing-hero-title
 *  - '/datenschutz' -> PrivacyPage.js   #privacy-title
 *  - '/agb'         -> TermsPage.js     #terms-title
 *  - '/mcp'         -> McpPage.js       #mcp-title
 * '/login', '/register' und '/forgot-password' laufen innerhalb von
 * TenantLayout und haben daher bereits #main-content.
 */
export const MARKETING_PAGE_HEADING_IDS = ['landing-hero-title', 'privacy-title', 'terms-title', 'mcp-title'];

/**
 * Fokussiert nach dem Ausblenden eines Fixed-Bottom-Banners ein sinnvolles
 * Ziel, statt den Fokus kommentarlos auf <body> "verloren gehen" zu lassen.
 *
 * #main-content existiert NUR innerhalb von TenantLayout (z.B. /login,
 * /register) — auf den eigenständigen Marketing-Seiten (Landing,
 * Datenschutz, AGB, MCP) gibt es dieses Element nicht. Fallback: eines der
 * bekannten <h1>-Elemente aus MARKETING_PAGE_HEADING_IDS, das dafür bei
 * Bedarf ein tabIndex-Attribut erhält. Existiert auch das nicht (z.B. eine
 * künftige Marketing-Seite ohne eingetragene ID), greift als letzter
 * Fallback das erste <h1> der Seite. Findet sich gar kein Ziel, wird der
 * Fokus bewusst NICHT erzwungen.
 *
 * Ein direkter Aufruf (kein focusWithRetry) genügt hier: Ziel-Element
 * existiert bereits vor UND nach dem Ausblenden des Banners unverändert im
 * DOM — anders als z.B. bei GalleryItemContextMenu, wo das Zielelement erst
 * durch einen State-Wechsel neu gerendert wird.
 */
export const moveFocusAfterDismiss = () => {
  const main = document.getElementById('main-content');
  if (main) {
    main.focus();
    return;
  }
  const heading =
    MARKETING_PAGE_HEADING_IDS.map((id) => document.getElementById(id)).find(Boolean) ||
    document.querySelector('h1');
  if (heading) {
    if (!heading.hasAttribute('tabindex')) {
      heading.setAttribute('tabindex', '-1');
    }
    heading.focus();
  }
};
